import React, { useState } from 'react';
import cn from 'classnames';

import { USER_ROLES } from 'constants/index';

import { selectIsShowShares } from 'client/redux/ui/selectors';
import { selectNeedToRecord, selectRoomStatus } from 'client/redux/room/selectors';
import { selectIsBroadcasting, selectMuxDetailsIsBusy } from 'client/redux/mux-details/selectors';
import { selectOccupancy, selectRaiseHandUsersTotal, selectSignedUsersOccupancy } from 'client/redux/pubnub/selectors';

import { startBroadcast, stopBroadcast } from 'client/redux/mux-details/actions';

import { ROOM_STATUS } from 'constants/index';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import {
  useAppDispatch,
  useOnClickOutside,
  useAppSelector,
  useTranslation,
  useToggleMicrophone,
  useReactionsCount,
  usePrevious,
} from 'client/hooks';
import { roomInfoLS } from 'client/utils';
import { eventBus } from 'client/utils/event-bus';
import { analytics } from 'client/utils/analytics';
import { selectUserName } from 'client/redux/client/selectors';
import { useGAEventCommonProps } from 'client/hooks/ga-event-common-props';
import TransitionComponent from 'client/components/common/TransitionComponent';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';

import { MicWarning } from '../../MicWarning';
import ProgressLoader from './ProgressLoader';
import BroadcastButton from './BroadcastButton';
import MicrophoneButton from './MicrophoneButton';
import SharesActions from '../../../SharesActions';
import Hints, { HintItemType } from '../../../Hints';
import StopBroadcastPopup from '../../../Popups/StopBroadcastPopup';
import ClosedRoomForHostPopup from '../../../Popups/ClosedRoomForHostPopup';
import ReactionsDetailsDrawer from '../../../Drawers/ReactionsDetailsDrawer';
import ListenersDetailsDrawer from '../../../Drawers/ListenersDetailsDrawer';
import ReactionsDetailsDrawerButton from '../../../Drawers/ReactionsDetailsDrawer/ReactionsDetailsDrawerButton';
import ListenersDetailsDrawerButton from '../../../Drawers/ListenersDetailsDrawer/ListenersDetailsDrawerButton';

import css from './AdminActionsPanel.module.scss';

interface Props {
  className?: string;
  roomId: string;
  userRole: USER_ROLES;
}

const AdminActionsPanel: React.FC<Props> = (props) => {
  const { className = '', roomId, userRole } = props;
  const dispatch = useAppDispatch();
  const { common } = useGAEventCommonProps();
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const userName = useAppSelector(selectUserName);
  const roomStatus = useAppSelector(selectRoomStatus);
  const roomIsNeedRecord = useAppSelector(selectNeedToRecord);
  const { state, dispatch: liveStreamDispatch } = useLiveStreamContext();
  const { total: totalReactions } = useReactionsCount();
  const { isBusy: liveStreamIsBusy } = state;

  const pendingRequestsCount = useAppSelector(selectRaiseHandUsersTotal);
  const isShowShares = useAppSelector(selectIsShowShares);
  const isBusy = useAppSelector(selectMuxDetailsIsBusy);
  const counterIncognito = useAppSelector(selectOccupancy);
  const counterLoggedIn = useAppSelector(selectSignedUsersOccupancy);
  const counter = counterIncognito + counterLoggedIn;

  const signedUsersOccupancy = useAppSelector(selectSignedUsersOccupancy);
  const prevIsBroadcasting = usePrevious<boolean>(isBroadcasting);

  const { translate: translateHints } = useTranslation('hints');
  const { translate: translateActions } = useTranslation('actionsPanel');
  const { translate: translatePopUps } = useTranslation('popUps');
  const { unMuteMic, muteMic } = useToggleMicrophone();
  const [currentHintIndex, setCurrentHintIndex] = useState(-1);

  const [isClosedHint, setIsClosedHint] = useState<boolean>(true);
  const [isReactionsDrawerOpen, setIsReactionsDrawerOpen] = useState<boolean>(false);
  const [isListenersDetailsDrawerOpen, setIsListenersDetailsDrawerOpen] = useState<boolean>(false);
  const [isShowEndBroadCastPopup, setIsShowEndBroadCastPopup] = useState<boolean>(false);
  const [isProgressVisible, setIsProgressVisible] = useState<boolean>(false);
  const [isShowHostFinishedPopup, setIsShowHostFinishedPopup] = useState<boolean>(false);
  const [isMyMicMuted, setIsMyMicMuted] = useState<boolean>(true);

  const isRoomFinished = roomStatus === ROOM_STATUS.FINISHED;

  const listenersWrapperDrawerRef = React.useRef<HTMLDivElement>(null);

  const hints = React.useMemo(() => {
    const hintsData: HintItemType[] = [
      {
        icon: 'recording-helper',
        title: translateHints(`${userRole}.initialHint.title`, { name: userName }),
        text: translateHints(`${userRole}.initialHint.text`),
      },
      {
        icon: userRole === USER_ROLES.HOST ? 'red_microphone' : '',
        title: translateHints(`${userRole}.afterActivateHint.title`),
        text: translateHints(`${userRole}.afterActivateHint.text`),
      },
      {
        icon: userRole === USER_ROLES.HOST ? 'red_microphone' : '',
        title: translateHints(`${userRole}.activatedByAnotherHint.title`, { name: userName }),
        text: translateHints(`${userRole}.activatedByAnotherHint.text`),
      },
    ];

    return hintsData;
  }, [userRole, translateHints]);

  React.useEffect(() => {
    if (roomStatus === ROOM_STATUS.ACTIVE) {
      dispatch(setIsShowFooterPopUpOverlay(true));
      setIsClosedHint(false);
    }
  }, [dispatch, roomStatus, isBroadcasting]);

  React.useEffect(() => {
    const lsRoomInfo = roomInfoLS.getData(roomId);

    if (prevIsBroadcasting && !isBroadcasting && lsRoomInfo?.isStartBroadcastByMe) {
      roomInfoLS.setData(roomId, { isStartBroadcastByMe: false });
    }

    if (isBroadcasting && lsRoomInfo?.isStartBroadcastByMe) {
      setCurrentHintIndex(1);
    } else if (isBroadcasting && !lsRoomInfo?.isStartBroadcastByMe) {
      setCurrentHintIndex(2);
    } else if (!isBroadcasting) {
      setCurrentHintIndex(0);
    }

    // roomId do not change during user use the app
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroadcasting]);

  React.useEffect(() => {
    if (liveStreamIsBusy) {
      setIsProgressVisible(true);
    }
  }, [liveStreamIsBusy]);

  React.useEffect(() => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const isShowFinishPopup = lsRoomInfo?.isShowHostFinishedPopup;
    const isHost = userRole === USER_ROLES.HOST;

    if (roomStatus === ROOM_STATUS.FINISHED && isHost && !isShowFinishPopup) {
      setIsShowHostFinishedPopup(true);
      dispatch(setIsShowFooterPopUpOverlay(true));
    }
  }, [roomStatus]);

  React.useEffect(() => {
    const unsubscribe = eventBus.subscribe<{ isMuted: boolean }>('TOGGLE_LOCAL_MIC', onToggleLocalMic);

    return () => {
      unsubscribe();
    };
  }, []);

  const onToggleLocalMic = (data?: { isMuted: boolean }) => {
    if (!data) return;
    setIsMyMicMuted(data.isMuted);
  };

  const hideProgress = () => {
    setIsProgressVisible(false);
  };

  const broadcastHandler = async () => {
    if (!isBroadcasting) {
      liveStreamDispatch({ type: 'SET_IS_BUSY', payload: true });

      await dispatch(
        startBroadcast({
          roomId,
          withRecording: roomIsNeedRecord,
        }),
      );
      roomInfoLS.setData(roomId, { isStartBroadcastByMe: true });

      analytics.gtag.event('audio_chat_operator_interaction', {
        ...common,
        start_chat_timestamp: Date.now(),
      });
    } else {
      setIsShowEndBroadCastPopup(true);
      dispatch(setIsShowFooterPopUpOverlay(true));
    }
  };

  const onHintClose = () => {
    setIsClosedHint(true);
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  const broadcastMicHandler = async () => {
    if (isMyMicMuted) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.hostMicUnmutedBtn'),
      });
      unMuteMic();

      onHintClose();
    } else {
      console.info('Disable Mic');
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.hostMicMutedBtn'),
      });
      muteMic();
    }
  };

  const closeReactionsDrawer = () => {
    if (isReactionsDrawerOpen) {
      setIsReactionsDrawerOpen(false);
    }
  };

  const listenersDetailsDrawerHandler = () => {
    if (!isListenersDetailsDrawerOpen) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.usersStatisticBtn'),
      });
    }
    setIsListenersDetailsDrawerOpen(!isListenersDetailsDrawerOpen);
  };

  const closeListenersDrawer = () => {
    if (isListenersDetailsDrawerOpen) {
      setIsListenersDetailsDrawerOpen(false);
    }
  };

  const openReactionsDrawer = () => {
    if (!isReactionsDrawerOpen) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.detailedReactionsBtn'),
      });
      setIsReactionsDrawerOpen(true);
    }
  };

  const onStopBroadcastSubmit = async () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translatePopUps('closeRoomPopUp.agreeBtn'),
    });

    liveStreamDispatch({ type: 'SET_IS_BUSY', payload: true });

    await dispatch(
      stopBroadcast({
        roomId,
      }),
    );

    analytics.gtag.event('audio_chat_operator_interaction', {
      ...common,
      end_chat_timestamp: Date.now(),
    });

    roomInfoLS.setData(roomId, { isStartBroadcastByMe: false });
    onCloseStopBroadcastPopUp();
  };

  const onCloseStopBroadcastPopUp = () => {
    setIsShowEndBroadCastPopup(false);
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  const onCloseStopBroadcastPopUpClick = () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translatePopUps('closeRoomPopUp.cancelBtn'),
    });

    onCloseStopBroadcastPopUp();
  };

  const onCloseClosedRoomForHostPopUp = () => {
    setIsShowHostFinishedPopup(false);
    dispatch(setIsShowFooterPopUpOverlay(false));
    roomInfoLS.setData(roomId, { isShowHostFinishedPopup: true });
  };

  useOnClickOutside<HTMLDivElement>(listenersWrapperDrawerRef, closeListenersDrawer);

  return (
    <div className={cn(css.actionPanel, userRole === USER_ROLES.HOST && css.isHostsPanel, className)}>
      <div className={cn(css.actionPanelI)}>
        <div className={cn(css.actionWrapper, css.sharesWrapper, isShowShares && css.biggestIndex)}>
          <div className={cn(css.action, css.shares)}>
            <SharesActions />
          </div>
        </div>
        <div className={cn(css.actionWrapper, css.broadcastHandlerWrapper)}>
          <div
            className={cn(
              css.action,
              css.broadcastHandler,
              userRole === USER_ROLES.HOST && isBroadcasting && css.isStarted,
              isRoomFinished && css.finished,
              !isRoomFinished && (isBusy || liveStreamIsBusy) && css.disabled,
            )}
            onClick={broadcastHandler}
          >
            <BroadcastButton isRoomFinished={isRoomFinished} isBroadcasting={isBroadcasting} userRole={userRole} />
          </div>
          {isProgressVisible && <ProgressLoader liveStreamIsBusy={liveStreamIsBusy} hideProgress={hideProgress} />}
        </div>

        {userRole === USER_ROLES.HOST && (
          <div className={cn(css.actionWrapper, css.micConnectionWrapper)}>
            <div
              className={cn(css.action, css.micConnection, isRoomFinished && css.finished)}
              onClick={broadcastMicHandler}
            >
              <MicWarning
                className={cn(css.micWarning, isBroadcasting && css.micActivePosition)}
                size={isBroadcasting ? 'large' : 'default'}
              />
              <MicrophoneButton
                isRoomFinished={isRoomFinished}
                isMicMuted={isMyMicMuted}
                isBroadcasting={isBroadcasting}
              />
            </div>
          </div>
        )}

        <div className={cn(css.actionWrapper, css.reactionsStatisticsWrapper)}>
          <div className={cn(css.action, css.reactionsStatistics)}>
            <ReactionsDetailsDrawerButton
              totalReactions={totalReactions}
              openReactionsDrawer={openReactionsDrawer}
              isReactionsDrawerOpen={isReactionsDrawerOpen}
            />
          </div>

          <ReactionsDetailsDrawer
            visible={isReactionsDrawerOpen}
            handleClose={closeReactionsDrawer}
            userRole={userRole}
            pendingRequestsCount={pendingRequestsCount}
            connectedListenersCount={signedUsersOccupancy}
            totalListenersCount={counter}
          />
        </div>
        <div className={cn(css.actionWrapper, css.listenersDetailsWrapper)} ref={listenersWrapperDrawerRef}>
          <div
            className={cn(css.action, css.listenersDetails, isListenersDetailsDrawerOpen && css.active)}
            onClick={listenersDetailsDrawerHandler}
          >
            <ListenersDetailsDrawerButton
              counter={counter}
              isListenersDetailsDrawerOpen={isListenersDetailsDrawerOpen}
            />
          </div>

          <ListenersDetailsDrawer
            visible={isListenersDetailsDrawerOpen}
            handleClose={closeListenersDrawer}
            userRole={userRole}
            pendingRequestsCount={pendingRequestsCount}
            connectedListenersCount={signedUsersOccupancy}
            totalListenersCount={counter}
          />
        </div>
      </div>
      {hints && (
        <TransitionComponent type="opacity" visible={!isClosedHint}>
          <Hints
            hints={hints}
            next={currentHintIndex}
            handleClose={onHintClose}
            hintClassName={userRole === USER_ROLES.HOST ? cn(css.hint, isBroadcasting && css.hintOnAir) : ''}
          />
        </TransitionComponent>
      )}
      <TransitionComponent type="opacity" visible={isShowEndBroadCastPopup}>
        <StopBroadcastPopup
          onSubmit={onStopBroadcastSubmit}
          onCancel={onCloseStopBroadcastPopUpClick}
          isLiveStreamBusy={liveStreamIsBusy}
        />
      </TransitionComponent>
      <TransitionComponent type="opacity" visible={isShowHostFinishedPopup}>
        <ClosedRoomForHostPopup onCancel={onCloseClosedRoomForHostPopUp} />
      </TransitionComponent>
    </div>
  );
};

export default AdminActionsPanel;
